<script setup>
defineProps({
  kind: {
    type: String,
    required: true,
    validator: value => ['inbox', 'team', 'canned', 'labels'].includes(value),
  },
  label: { type: String, required: true },
  items: { type: Array, default: () => [] },
});
</script>

<template>
  <svg
    v-if="kind === 'inbox'"
    viewBox="0 0 240 144"
    role="img"
    :aria-label="label"
    class="h-36 w-full max-w-[15rem] text-n-slate-11"
  >
    <title>{{ label }}</title>
    <rect
      x="16"
      y="16"
      width="208"
      height="112"
      rx="12"
      class="fill-n-solid-2 stroke-n-weak"
      stroke-width="2"
    />
    <rect x="34" y="32" width="76" height="22" rx="6" class="fill-n-brand" />
    <rect x="34" y="64" width="76" height="22" rx="6" class="fill-n-alpha-3" />
    <rect x="34" y="96" width="76" height="22" rx="6" class="fill-n-alpha-3" />
    <rect x="130" y="32" width="76" height="22" rx="6" class="fill-n-alpha-3" />
    <rect x="130" y="64" width="76" height="22" rx="6" class="fill-n-alpha-3" />
    <rect x="130" y="96" width="76" height="22" rx="6" class="fill-n-alpha-3" />
    <foreignObject x="39" y="36" width="66" height="15">
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        class="flex items-center gap-1 text-[8px] font-medium text-n-solid-1"
      >
        <span class="i-lucide-webhook size-3 shrink-0" />
        <span class="truncate">{{ items[0] }}</span>
      </div>
    </foreignObject>
    <foreignObject x="39" y="68" width="66" height="15">
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        class="flex items-center gap-1 text-[8px] font-medium text-n-slate-11"
      >
        <span class="i-ri-whatsapp-fill size-3 shrink-0 text-n-brand" />
        <span class="truncate">{{ items[1] }}</span>
      </div>
    </foreignObject>
    <foreignObject x="39" y="100" width="66" height="15">
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        class="flex items-center gap-1 text-[8px] font-medium text-n-slate-11"
      >
        <span class="i-ri-telegram-fill size-3 shrink-0 text-n-brand" />
        <span class="truncate">{{ items[2] }}</span>
      </div>
    </foreignObject>
    <foreignObject x="135" y="36" width="66" height="15">
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        class="flex items-center gap-1 text-[8px] font-medium text-n-slate-11"
      >
        <span class="i-ri-facebook-circle-fill size-3 shrink-0 text-n-brand" />
        <span class="truncate">{{ items[3] }}</span>
      </div>
    </foreignObject>
    <foreignObject x="135" y="68" width="66" height="15">
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        class="flex items-center gap-1 text-[8px] font-medium text-n-slate-11"
      >
        <span class="i-ri-instagram-fill size-3 shrink-0 text-n-brand" />
        <span class="truncate">{{ items[4] }}</span>
      </div>
    </foreignObject>
    <foreignObject x="135" y="100" width="66" height="15">
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        class="flex items-center gap-1 text-[8px] font-medium text-n-slate-11"
      >
        <span class="i-lucide-mail size-3 shrink-0 text-n-brand" />
        <span class="truncate">{{ items[5] }}</span>
      </div>
    </foreignObject>
  </svg>
  <svg
    v-else-if="kind === 'team'"
    viewBox="0 0 240 144"
    role="img"
    :aria-label="label"
    class="h-36 w-full max-w-[15rem] text-n-slate-11"
  >
    <title>{{ label }}</title>
    <rect
      x="24"
      y="24"
      width="92"
      height="40"
      rx="10"
      class="fill-n-alpha-3 stroke-n-brand"
      stroke-width="2"
    />
    <rect
      x="124"
      y="24"
      width="92"
      height="40"
      rx="10"
      class="fill-n-alpha-3 stroke-n-weak"
      stroke-width="2"
    />
    <rect
      x="24"
      y="80"
      width="92"
      height="40"
      rx="10"
      class="fill-n-alpha-3 stroke-n-weak"
      stroke-width="2"
    />
    <rect
      x="124"
      y="80"
      width="92"
      height="40"
      rx="10"
      class="fill-n-alpha-3 stroke-n-weak"
      stroke-width="2"
    />
    <circle cx="46" cy="42" r="11" class="fill-n-brand" />
    <circle cx="146" cy="42" r="11" class="fill-n-slate-10" />
    <circle cx="46" cy="98" r="11" class="fill-n-slate-10" />
    <circle cx="146" cy="98" r="11" class="fill-n-slate-10" />
    <circle cx="46" cy="39" r="4" class="fill-n-solid-1" />
    <circle cx="146" cy="39" r="4" class="fill-n-solid-1" />
    <circle cx="46" cy="95" r="4" class="fill-n-solid-1" />
    <circle cx="146" cy="95" r="4" class="fill-n-solid-1" />
    <path
      d="M39 49c2-5 12-5 14 0M139 49c2-5 12-5 14 0M39 105c2-5 12-5 14 0M139 105c2-5 12-5 14 0"
      class="fill-none stroke-n-solid-1"
      stroke-width="2"
      stroke-linecap="round"
    />
    <text x="62" y="46" class="fill-current text-[8px] font-medium">
      {{ items[0] }}
    </text>
    <text x="162" y="46" class="fill-current text-[8px] font-medium">
      {{ items[1] }}
    </text>
    <text x="62" y="102" class="fill-current text-[8px] font-medium">
      {{ items[2] }}
    </text>
    <text x="162" y="102" class="fill-current text-[8px] font-medium">
      {{ items[3] }}
    </text>
  </svg>
  <svg
    v-else-if="kind === 'canned'"
    viewBox="0 0 240 144"
    role="img"
    :aria-label="label"
    class="h-36 w-full max-w-[15rem] text-n-slate-11"
  >
    <title>{{ label }}</title>
    <rect
      x="25"
      y="18"
      width="190"
      height="108"
      rx="12"
      class="fill-n-solid-2 stroke-n-weak"
      stroke-width="2"
    />
    <rect x="42" y="31" width="106" height="17" rx="6" class="fill-n-alpha-3" />
    <rect
      x="42"
      y="53"
      width="132"
      height="28"
      rx="7"
      class="fill-n-alpha-3 stroke-n-brand"
      stroke-width="1.5"
    />
    <circle cx="56" cy="67" r="5" class="fill-n-brand" />
    <path
      d="m54 67 2 2 4-5"
      class="fill-none stroke-n-solid-1"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <text x="52" y="43" class="fill-current text-[8px] font-medium">
      /{{ items[0] }}
    </text>
    <text x="67" y="70" class="fill-current text-[8px] font-medium">
      /{{ items[1] }}
    </text>
    <rect
      x="42"
      y="91"
      width="132"
      height="20"
      rx="6"
      class="fill-n-alpha-3 stroke-n-weak"
      stroke-width="1.5"
    />
    <text x="52" y="104" class="fill-current text-[8px] font-medium">
      /{{ items[2] }}
    </text>
    <rect x="180" y="91" width="18" height="20" rx="6" class="fill-n-brand" />
    <path d="m184 101 9-5-3 10-2-4z" class="fill-n-solid-1" />
  </svg>
  <svg
    v-else
    viewBox="0 0 240 144"
    role="img"
    :aria-label="label"
    class="h-36 w-full max-w-[15rem] text-n-slate-11"
  >
    <title>{{ label }}</title>
    <path d="M26 25h92l18 18-18 18H26z" class="fill-n-brand" />
    <path d="M104 52h92l18 18-18 18h-92z" class="fill-n-alpha-3" />
    <path d="M26 79h92l18 18-18 18H26z" class="fill-n-alpha-3" />
    <path d="M104 106h92l18 18-18 18h-92z" class="fill-n-alpha-3" />
    <circle cx="42" cy="43" r="4" class="fill-n-solid-1" />
    <circle cx="120" cy="70" r="4" class="fill-n-brand" />
    <circle cx="42" cy="97" r="4" class="fill-n-brand" />
    <circle cx="120" cy="124" r="4" class="fill-n-brand" />
    <text x="51" y="47" class="fill-n-solid-1 text-[9px] font-medium">
      #{{ items[0] }}
    </text>
    <text x="129" y="74" class="fill-current text-[9px] font-medium">
      #{{ items[1] }}
    </text>
    <text x="51" y="101" class="fill-current text-[9px] font-medium">
      #{{ items[2] }}
    </text>
    <text x="129" y="128" class="fill-current text-[9px] font-medium">
      #{{ items[3] }}
    </text>
  </svg>
</template>
